import type { SetupStatus, SetupStepItem, SetupStepKey } from './setup-types.js';

/**
 * Sihirbaz adımlarının açtığı ekranlar (ADR 0042 §22) [sorular #247]: her adım verinin tutulduğu
 * var olan ekrandır; sihirbazın kendi tablosu ve menü öğesi yoktur.
 */
export const SETUP_STEP_PATHS: Readonly<Record<SetupStepKey, string>> = {
  company: '/settings',
  vessels: '/fleet',
  roles: '/settings/roles',
  permissions: '/settings/matrices',
  library: '/compliance/ism-forms/categories',
  approvals: '/settings/matrices?tab=approvals',
  users: '/settings/users',
  setupCodes: '/fleet',
};

export const SETUP_PATH = '/settings/setup';

/** Tamamlanmış sayılan adım: yapılmış ya da varsayılanla (provisioning) gelmiş. */
export const stepComplete = (step: SetupStepItem) => step.status !== 'todo';

export function setupProgress(status: SetupStatus): {
  done: number;
  total: number;
  next: SetupStepItem | null;
} {
  return {
    done: status.steps.filter(stepComplete).length,
    total: status.steps.length,
    next: status.steps.find((step) => !stepComplete(step)) ?? null,
  };
}

const num = (value: unknown) => (typeof value === 'number' ? value : 0);

/** Adımın ayrıntı satırının çeviri anahtarı ve değerleri. */
export function stepDetail(step: SetupStepItem): { key: string; values: Record<string, unknown> } {
  const d = step.detail;
  const base = `setup.wizard.step.${step.key}`;
  switch (step.key) {
    case 'company':
      return d.name
        ? {
            key: `${base}.detail`,
            values: { name: d.name, currency: d.baseCurrency ?? '—' },
          }
        : { key: `${base}.missing`, values: {} };
    case 'vessels':
      return { key: `${base}.detail`, values: { count: num(d.count) } };
    case 'roles':
      return { key: `${base}.detail`, values: { count: num(d.count), custom: num(d.custom) } };
    case 'permissions':
    case 'approvals':
      return { key: `${base}.detail`, values: { version: num(d.version) } };
    case 'library':
      return {
        key: `${base}.detail`,
        values: {
          withTree: num(d.vesselsWithTree),
          vessels: num(d.vessels),
          documents: num(d.companyDocuments),
        },
      };
    case 'users':
      return {
        key: `${base}.detail`,
        values: {
          active: num(d.active),
          manned: num(d.vesselsWithMaster),
          vessels: num(d.vessels),
        },
      };
    case 'setupCodes':
      return {
        key: `${base}.detail`,
        values: { activated: num(d.activated), vessels: num(d.vessels) },
      };
  }
}
